import { Ellipsoid } from "../Ellipsoid";
import { Cartographic } from "../Cartographic";
import { Vector3 } from "three";
import { Rectangle } from "../Rectangle";
/**
 * The 'WebMercator' map projection scheme.
 *
 * @see MapProjection
 */
export class WebMercatorProjection {
    constructor(ellipsoid = Ellipsoid.WGS_84) {
        this._ellipsoid = ellipsoid;
        this._semimajorAxis = this._ellipsoid.maximumRadius;
        this._oneOverSemimajorAxis = 1.0 / this._semimajorAxis;
        this._region = new Rectangle(Rectangle.MAX.west, WebMercatorProjection.MIN_LATITUDE, Rectangle.MAX.east, WebMercatorProjection.MAX_LATITUDE);
    }
    /**
     * @see MapProjection.ellipsoid
     */
    get ellipsoid() {
        return this._ellipsoid;
    }
    get region() {
        return this._region;
    }
    /**
     * @see MapProjection.project
     */
    project(cartographic, result = new Vector3()) {
        const longitude = cartographic.longitude;
        const latitude = cartographic.latitude;
        const x = longitude * this._semimajorAxis;
        const y = WebMercatorProjection.geodeticLatitudeToMercatorAngle(latitude) * this._semimajorAxis;
        const z = cartographic.height;
        return result.set(x, y, z);
    }
    /**
     * @see MapProjection.unproject
     */
    unproject(cartesian, result = new Cartographic()) {
        const x = cartesian.x;
        const y = cartesian.y;
        const longitude = x * this._oneOverSemimajorAxis;
        const latitude = WebMercatorProjection.mercatorAngleToGeodeticLatitude(y * this._oneOverSemimajorAxis);
        const height = cartesian.z;
        return result.set(longitude, latitude, height);
    }
    /**
     * Converts a Mercator angle, in the range -PI to PI, to a geodetic latitude
     * in the range -PI/2 to PI/2.
     *
     * @param mercatorAngle The angle to convert.
     * @returns The geodetic latitude in radians.
     */
    static mercatorAngleToGeodeticLatitude(mercatorAngle) {
        return Math.PI / 2 - 2.0 * Math.atan(Math.exp(-mercatorAngle));
    }
    /**
     * Converts a geodetic latitude in radians, in the range -PI/2 to PI/2, to a Mercator
     * angle in the range -PI to PI.
     *
     * @param latitude The geodetic latitude in radians.
     * @returns The Mercator angle.
     */
    static geodeticLatitudeToMercatorAngle(latitude) {
        // Clamp the latitude coordinate to the valid Mercator bounds.
        latitude = Math.min(Math.max(latitude, this.MIN_LATITUDE), this.MAX_LATITUDE);
        const sinLatitude = Math.sin(latitude);
        return 0.5 * Math.log((1.0 + sinLatitude) / (1.0 - sinLatitude));
    }
}
/**
 * The maximum supported latitude in a WebMercator map in radians.
 */
WebMercatorProjection.MAX_LATITUDE = 1.4844217274;
/**
 * The minimum supported latitude in a WebMercator map in radians.
 */
WebMercatorProjection.MIN_LATITUDE = -1.4844217274;
